import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, Plus, MagnifyingGlass, Pencil, Trash, Package, X,
  Warning, Fingerprint, ArrowClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Duplicate Warning Panel ── */
const DuplicateWarning = ({ duplicates, hasWarning }) => {
  if (!duplicates || duplicates.length === 0) return null;
  return (
    <div className={`border rounded p-3 mt-3 ${hasWarning ? 'border-amber-400 bg-amber-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="duplicate-warning">
      <div className="flex items-center gap-1.5 mb-2">
        <Warning size={16} className={hasWarning ? 'text-amber-600' : 'text-zinc-400'} />
        <span className={`text-xs font-bold uppercase tracking-wide ${hasWarning ? 'text-amber-700' : 'text-zinc-500'}`}>
          {hasWarning ? 'Potential Duplicates Found' : 'Similar Materials'}
        </span>
      </div>
      <div className="space-y-1.5">
        {duplicates.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-[11px] bg-white border border-zinc-200 px-2.5 py-1.5 rounded">
            <div>
              <span className="font-mono text-blue-700 font-bold mr-2">{d.material_code}</span>
              <span className="text-zinc-800">{d.description}</span>
              {d.family && <span className="ml-2 text-zinc-400">{d.family}</span>}
              {d.brand && <span className="ml-1 text-zinc-400">/ {d.brand}</span>}
            </div>
            <div className="flex gap-2 items-center shrink-0">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${d.combined_score >= 70 ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-600'}`}>
                {d.combined_score}%
              </span>
              {d.fingerprint_match && (
                <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold flex items-center gap-0.5">
                  <Fingerprint size={10} /> FP Match
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {hasWarning && <p className="text-[10px] text-amber-600 mt-2 font-medium">Review matches above. You can still proceed if this is a distinct material.</p>}
    </div>
  );
};

/* ── Specification Fields (template-driven) ── */
const SpecFields = ({ family, specifications, onChange }) => {
  const [template, setTemplate] = useState(null);
  const [genericSpecs, setGenericSpecs] = useState([]);

  useEffect(() => {
    if (!family || family === 'Other') {
      setTemplate(null);
      return;
    }
    axios.get(`${API}/api/material-master/spec-template/${encodeURIComponent(family)}`, { withCredentials: true })
      .then(res => setTemplate(res.data))
      .catch(() => setTemplate(null));
  }, [family]);

  // Generic fallback: key-value pairs
  const addGenericRow = () => setGenericSpecs([...genericSpecs, { key: '', value: '' }]);
  const updateGenericRow = (idx, field, val) => {
    const next = [...genericSpecs];
    next[idx] = { ...next[idx], [field]: val };
    setGenericSpecs(next);
    const obj = {};
    next.forEach(r => { if (r.key.trim()) obj[r.key.trim()] = r.value; });
    onChange(obj);
  };
  const removeGenericRow = (idx) => {
    const next = genericSpecs.filter((_, i) => i !== idx);
    setGenericSpecs(next);
    const obj = {};
    next.forEach(r => { if (r.key.trim()) obj[r.key.trim()] = r.value; });
    onChange(obj);
  };

  // Initialize generic specs from existing specifications
  useEffect(() => {
    if ((!family || family === 'Other') && specifications && Object.keys(specifications).length > 0) {
      setGenericSpecs(Object.entries(specifications).map(([key, value]) => ({ key, value: String(value) })));
    }
  }, [family]);

  if (!family) return null;

  // Template-driven specs
  if (template && !template.is_generic && template.fields?.length > 0) {
    return (
      <div className="border-t border-zinc-200 pt-3 mt-3" data-testid="spec-fields">
        <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Specifications — {family}</h4>
        <div className="grid grid-cols-2 gap-3">
          {template.fields.map(f => (
            <div key={f.key}>
              <Label className="text-[10px] text-zinc-500">{f.label}</Label>
              {f.type === 'select' ? (
                <select
                  value={specifications[f.key] || ''}
                  onChange={e => onChange({ ...specifications, [f.key]: e.target.value })}
                  className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2 text-xs"
                  data-testid={`spec-${f.key}`}
                >
                  <option value="">Select...</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={specifications[f.key] || ''}
                  onChange={e => onChange({ ...specifications, [f.key]: f.type === 'number' ? (parseFloat(e.target.value) || '') : e.target.value })}
                  className="mt-1 rounded-none text-xs bg-zinc-50"
                  data-testid={`spec-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="border-t border-zinc-200 pt-3 mt-3" data-testid="spec-fields-generic">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Specifications (Custom)</h4>
        <button onClick={addGenericRow} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-0.5" data-testid="add-generic-spec">
          <Plus size={12} /> Add Field
        </button>
      </div>
      {genericSpecs.length === 0 && <p className="text-[10px] text-zinc-400 italic">No custom specs. Click "Add Field" to define.</p>}
      {genericSpecs.map((r, i) => (
        <div key={i} className="flex gap-2 mb-1.5 items-center">
          <Input value={r.key} onChange={e => updateGenericRow(i, 'key', e.target.value)} placeholder="Field name" className="rounded-none text-xs bg-zinc-50 flex-1" />
          <Input value={r.value} onChange={e => updateGenericRow(i, 'value', e.target.value)} placeholder="Value" className="rounded-none text-xs bg-zinc-50 flex-1" />
          <button onClick={() => removeGenericRow(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      ))}
    </div>
  );
};

const MaterialMaster = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['super_admin', 'admin'].includes(user?.role);

  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    description: '', category: '', unit: 'Nos', default_hsn: '',
    subcategory: '', family: '', variant_name: '', brand: '', remarks: '',
    specifications: {},
  });
  const [supplierMappings, setSupplierMappings] = useState([]);
  const [duplicates, setDuplicates] = useState({ duplicates: [], has_warning: false });
  const [dupeChecking, setDupeChecking] = useState(false);

  // Suppliers list
  const [suppliers, setSuppliers] = useState([]);
  const [ratesStatus, setRatesStatus] = useState(null);
  const [refreshingRates, setRefreshingRates] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/material-master-rates-status`, { withCredentials: true })
      .then(r => setRatesStatus(r.data)).catch(() => {});
  }, []);

  const handleRefreshRates = async () => {
    setRefreshingRates(true);
    try {
      const { data } = await axios.post(`${API}/api/material-master-refresh-rates`, {}, { withCredentials: true });
      toast.success(`Reference rates updated for ${data.updated} material(s) from PO history`);
      setRatesStatus({ last_refreshed_at: data.rates_updated_at, updated_count: data.updated });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to refresh rates'); }
    finally { setRefreshingRates(false); }
  };

  useEffect(() => { fetchData(); }, [search, filterCategory, showInactive]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterCategory) params.set('category', filterCategory);
      if (showInactive) params.set('active_only', 'false');
      const [matRes, catRes, famRes] = await Promise.all([
        axios.get(`${API}/api/material-master?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/material-master/categories`, { withCredentials: true }),
        axios.get(`${API}/api/material-master/families`, { withCredentials: true }),
      ]);
      setMaterials(matRes.data);
      setCategories(catRes.data.categories);
      setFamilies(famRes.data.families);
    } catch { toast.error('Failed to load materials'); }
    finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/api/vendors-suppliers?entity_type=supplier`, { withCredentials: true });
      setSuppliers(res.data);
    } catch { setSuppliers([]); }
  };

  const resetForm = () => {
    setForm({ description: '', category: '', unit: 'Nos', default_hsn: '', subcategory: '', family: '', variant_name: '', brand: '', remarks: '', specifications: {} });
    setSupplierMappings([]);
    setEditingId(null);
    setDuplicates({ duplicates: [], has_warning: false });
  };

  const openCreate = async () => {
    resetForm();
    await fetchSuppliers();
    setDialogOpen(true);
  };

  const openEdit = async (mat) => {
    setEditingId(mat.id);
    setForm({
      description: mat.description, category: mat.category, unit: mat.unit,
      default_hsn: mat.default_hsn || '', subcategory: mat.subcategory || '',
      family: mat.family || '', variant_name: mat.variant_name || '',
      brand: mat.brand || '', remarks: mat.remarks || '',
      specifications: mat.specifications || {},
    });
    setSupplierMappings(mat.supplier_mappings || []);
    setDuplicates({ duplicates: [], has_warning: false });
    await fetchSuppliers();
    setDialogOpen(true);
  };

  // Duplicate check with debounce
  const checkDuplicates = useCallback(async (desc, cat, fam, brd, specs) => {
    if (!desc || desc.length < 3) { setDuplicates({ duplicates: [], has_warning: false }); return; }
    setDupeChecking(true);
    try {
      const { data } = await axios.post(`${API}/api/material-master/check-duplicate`, {
        description: desc, category: cat, family: fam, brand: brd,
        specifications: specs, exclude_id: editingId || undefined,
      }, { withCredentials: true });
      setDuplicates(data);
    } catch { /* ignore */ }
    setDupeChecking(false);
  }, [editingId]);

  // Debounced duplicate check on description change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.description.length >= 3) {
        checkDuplicates(form.description, form.category, form.family, form.brand, form.specifications);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.description, form.category, form.family, form.brand, form.specifications, checkDuplicates]);

  const addSupplierRow = () => {
    setSupplierMappings([...supplierMappings, { supplier_id: '', supplier_name: '', supplier_item_code: '', supplier_item_description: '' }]);
  };
  const updateSupplierRow = (idx, field, value) => {
    const updated = [...supplierMappings];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'supplier_id' && value) {
      const sup = suppliers.find(s => s.id === value);
      if (sup) updated[idx].supplier_name = sup.legal_name;
    }
    setSupplierMappings(updated);
  };
  const removeSupplierRow = (idx) => setSupplierMappings(supplierMappings.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.category) { toast.error('Category is required'); return; }
    if (form.default_hsn) {
      const clean = form.default_hsn.replace(/\s/g, '');
      if (clean.length < 6 || !/^\d+$/.test(clean)) { toast.error('HSN must be at least 6 digits'); return; }
    }
    try {
      const payload = { ...form, supplier_mappings: supplierMappings.filter(m => m.supplier_name) };
      if (editingId) {
        await axios.put(`${API}/api/material-master/${editingId}`, payload, { withCredentials: true });
        toast.success('Material updated');
      } else {
        await axios.post(`${API}/api/material-master`, payload, { withCredentials: true });
        toast.success('Material created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this material?')) return;
    try {
      await axios.delete(`${API}/api/material-master/${id}`, { withCredentials: true });
      toast.success('Material deactivated'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={20} className="mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Material Master</h1>
          <p className="text-sm text-zinc-600 mt-1">Central material catalogue with specifications, HSN codes, and supplier mapping</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by description or code..." className="rounded-none pl-10 bg-white" data-testid="material-search" />
          </div>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="category-filter">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded border-zinc-300 h-3.5 w-3.5" />
            Show Inactive
          </label>
          {canEdit && (
            <Button onClick={handleRefreshRates} disabled={refreshingRates} variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-none" data-testid="refresh-rates-button"
              title={ratesStatus?.last_refreshed_at ? `Last updated: ${new Date(ratesStatus.last_refreshed_at).toLocaleString('en-IN')}` : 'Never updated'}>
              <ArrowClockwise size={16} className={`mr-1.5 ${refreshingRates ? 'animate-spin' : ''}`} />
              {refreshingRates ? 'Refreshing…' : 'Refresh Rates'}
            </Button>
          )}
          {canEdit && (
            <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="add-material-button">
              <Plus size={18} className="mr-1.5" /> Add Material
            </Button>
          )}
        </div>
        {ratesStatus?.last_refreshed_at && (
          <p className="text-[11px] text-zinc-500 -mt-3 mb-4" data-testid="rates-last-updated">
            Reference rates last updated {new Date(ratesStatus.last_refreshed_at).toLocaleString('en-IN')} · {ratesStatus.updated_count} material(s) priced from PO history
          </p>
        )}

        {/* Stats */}
        <div className="flex gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-3 flex-1">
            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Total Materials</p>
            <p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="total-materials">{materials.length}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-3 flex-1">
            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">With Specs</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{materials.filter(m => m.spec_fingerprint).length}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-3 flex-1">
            <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Families</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{[...new Set(materials.filter(m => m.family).map(m => m.family))].length}</p>
          </div>
        </div>

        {/* Materials List */}
        {materials.length === 0 ? (
          <div className="text-center py-16 bg-white border border-zinc-200" data-testid="empty-materials">
            <Package size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500">No materials found. {canEdit ? 'Click "Add Material" to create one.' : ''}</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200 text-xs font-bold uppercase tracking-wider text-zinc-500">
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-left p-3">Family</th>
                  <th className="text-left p-3">Brand</th>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-left p-3">Fingerprint</th>
                  <th className="text-left p-3">Status</th>
                  {canEdit && <th className="text-left p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {materials.map(mat => (
                  <tr key={mat.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${!mat.is_active ? 'opacity-50' : ''}`} data-testid={`material-row-${mat.id}`}>
                    <td className="p-3 font-mono text-xs font-bold text-blue-700">{mat.material_code}</td>
                    <td className="p-3">
                      <span className="font-medium text-zinc-800">{mat.description}</span>
                      {mat.variant_name && <span className="block text-[10px] text-zinc-400">{mat.variant_name}</span>}
                    </td>
                    <td className="p-3">
                      {mat.family ? (
                        <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 border border-blue-200">{mat.family}</span>
                      ) : (
                        <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 border border-zinc-200">{mat.category}</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-zinc-600">{mat.brand || '-'}</td>
                    <td className="p-3 text-zinc-600">{mat.unit}</td>
                    <td className="p-3">
                      {mat.spec_fingerprint ? (
                        <span className="font-mono text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 border border-emerald-200 inline-block max-w-[160px] truncate" title={mat.spec_fingerprint}>
                          {mat.spec_fingerprint}
                        </span>
                      ) : <span className="text-zinc-400 text-xs">-</span>}
                    </td>
                    <td className="p-3">{mat.is_active ? <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200">Active</span> : <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 border border-red-200">Inactive</span>}</td>
                    {canEdit && (
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(mat)} className="p-1.5 text-blue-700 hover:bg-blue-50 border border-blue-200" data-testid={`edit-material-${mat.id}`}><Pencil size={14} /></button>
                          {mat.is_active && <button onClick={() => handleDeactivate(mat.id)} className="p-1.5 text-red-600 hover:bg-red-50 border border-red-200" data-testid={`deactivate-material-${mat.id}`}><Trash size={14} /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-900">{editingId ? 'Edit Material' : 'Add New Material'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Core Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description *</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-description-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Category *</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full bg-zinc-50 border border-zinc-200 rounded-none p-3 text-base" data-testid="mat-category-select">
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Family</Label>
                <select value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value, specifications: {} })} className="mt-2 w-full bg-zinc-50 border border-zinc-200 rounded-none p-3 text-base" data-testid="mat-family-select">
                  <option value="">Select Family</option>
                  {families.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <p className="text-[10px] text-zinc-400 mt-1">Drives specification template and dedup</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Subcategory</Label>
                <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Interior, Exterior" className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-subcategory-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Variant Name</Label>
                <Input value={form.variant_name} onChange={(e) => setForm({ ...form, variant_name: e.target.value })} placeholder="e.g. 12.5mm MR Board" className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-variant-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Brand</Label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Saint-Gobain" className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-brand-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Unit *</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-unit-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Default HSN Code</Label>
                <Input value={form.default_hsn} onChange={(e) => setForm({ ...form, default_hsn: e.target.value })} placeholder="e.g. 441231" className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-hsn-input" />
                <p className="text-[10px] text-zinc-400 mt-1">Minimum 6 digits</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Remarks</Label>
                <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Any notes..." className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3" data-testid="mat-remarks-input" />
              </div>
            </div>

            {/* Template-driven Specifications */}
            <SpecFields family={form.family} specifications={form.specifications} onChange={(specs) => setForm({ ...form, specifications: specs })} />

            {/* Duplicate Warning */}
            <DuplicateWarning duplicates={duplicates.duplicates} hasWarning={duplicates.has_warning} />

            {/* Supplier Mappings */}
            <div className="border-t border-zinc-200 pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Supplier Code Mapping</h3>
                <Button onClick={addSupplierRow} variant="outline" size="sm" className="border-zinc-300 rounded-none text-xs" data-testid="add-supplier-mapping">
                  <Plus size={14} className="mr-1" /> Add Supplier
                </Button>
              </div>
              {supplierMappings.length === 0 ? (
                <p className="text-xs text-zinc-400">No supplier mappings. Click "Add Supplier" to map.</p>
              ) : (
                <div className="space-y-3">
                  {supplierMappings.map((m, idx) => (
                    <div key={idx} className="bg-zinc-50 border border-zinc-200 p-3" data-testid={`supplier-mapping-${idx}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold text-zinc-500">Supplier #{idx + 1}</span>
                        <button onClick={() => removeSupplierRow(idx)} className="text-red-600 hover:bg-red-50 p-0.5"><X size={14} /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-zinc-500">Supplier</Label>
                          {suppliers.length > 0 ? (
                            <select value={m.supplier_id} onChange={(e) => updateSupplierRow(idx, 'supplier_id', e.target.value)} className="mt-1 w-full bg-white border border-zinc-200 rounded-none p-2 text-xs">
                              <option value="">Select or type name</option>
                              {suppliers.map(s => <option key={s.id} value={s.id}>{s.legal_name}</option>)}
                            </select>
                          ) : (
                            <Input value={m.supplier_name} onChange={(e) => updateSupplierRow(idx, 'supplier_name', e.target.value)} placeholder="Supplier name" className="mt-1 rounded-none text-xs bg-white" />
                          )}
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Supplier Item Code</Label>
                          <Input value={m.supplier_item_code} onChange={(e) => updateSupplierRow(idx, 'supplier_item_code', e.target.value)} placeholder="e.g. PLY-001" className="mt-1 rounded-none text-xs bg-white" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] text-zinc-500">Supplier Item Description</Label>
                          <Input value={m.supplier_item_description} onChange={(e) => updateSupplierRow(idx, 'supplier_item_description', e.target.value)} placeholder="Supplier's description for this material" className="mt-1 rounded-none text-xs bg-white" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={handleSubmit} className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5 mt-4" data-testid="submit-material-button">
              {editingId ? 'Update Material' : 'Add Material'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialMaster;
